import React from 'react';

import { Link as LinkIcon } from '@mui/icons-material';
import { Button } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { PageSimpleLayout } from 'src/components/common/PageSimpleLayout/PageSimpleLayout';
import { EmployeeDataSearchScreen } from 'src/components/employee';
import {
  useCopyEmployeeInviteLink,
  useEmployeeDataSearch,
  useResendEmployeeInvite,
} from 'src/hooks/employee';
import { useCurrentEmployerStaff } from 'src/hooks/employerStaff';
import { useCurrentPeoPayrollProviderStaff } from 'src/hooks/peoPayrollProviderStaff';
import { useCurrentPeoStaff } from 'src/hooks/peoStaff';
import { useCurrentUserPermissions } from 'src/hooks/user';

import { NotFoundErrorPage } from '../not-found';

export const EmployeesPage: React.FC = () => {
  const {
    isEmployerAdmin,
    isEmployerManager,
    isPeoSuperAdmin,
    isPeoAdmin,
    isPeoManager,
    isPeoPayrollProviderUser,
    hasPlatformRoles,
  } = useCurrentUserPermissions();

  if (hasPlatformRoles) {
    return (
      <PageSimpleLayout titleContent="Employees">
        <AdminEmployeesSection />
      </PageSimpleLayout>
    );
  }

  if (isEmployerAdmin || isEmployerManager) {
    return <EmployerEmployeesPage hideInviteLink={isEmployerManager} />;
  }

  if (isPeoSuperAdmin || isPeoAdmin || isPeoManager) {
    return <PeoEmployeesPage hideInviteLink={isPeoManager} />;
  }

  if (isPeoPayrollProviderUser) {
    return <PeoPayrollProviderEmployeesPage />;
  }

  return <NotFoundErrorPage />;
};

const AdminEmployeesSection: React.FC = () => {
  const search = useEmployeeDataSearch({ scope: 'admin' });
  const resendInvite = useResendEmployeeInvite('cvStaff');

  return (
    <EmployeeDataSearchScreen
      {...search}
      allowEmailSearch
      enableAdminControls
      inviteEmailOptionsScope="admin"
      sendInviteScope="cvStaff"
      onResendInvite={resendInvite}
    />
  );
};

type EmployerEmployeesPageProps = { hideInviteLink: boolean };

const EmployerEmployeesPage: React.FC<EmployerEmployeesPageProps> = ({ hideInviteLink }) => {
  const { employerId } = useCurrentEmployerStaff();
  const search = useEmployeeDataSearch({ scope: 'employerScope' });

  const inviteLinkArgs = {
    route: appRoutes.auth.signupEmployee,
    paramKey: RouterEntitiesId.employer,
    entityId: employerId,
  };
  const copyInviteLink = useCopyEmployeeInviteLink(inviteLinkArgs);
  const resendInvite = useResendEmployeeInvite('employer');

  return (
    <PageSimpleLayout
      titleContent="Employees"
      topRightContent={
        hideInviteLink ? null : (
          <Button key="copyInviteLink" startIcon={<LinkIcon />} onClick={copyInviteLink}>
            Copy signup link
          </Button>
        )
      }
    >
      <EmployeeDataSearchScreen
        {...search}
        hideEmployerLiveIndicator
        allowEmailSearch
        inviteLinkArgs={inviteLinkArgs}
        inviteEmailOptionsScope="employerScope"
        sendInviteScope="employer"
        onResendInvite={resendInvite}
      />
    </PageSimpleLayout>
  );
};

type PeoEmployeesPageProps = { hideInviteLink: boolean };

const PeoEmployeesPage: React.FC<PeoEmployeesPageProps> = ({ hideInviteLink }) => {
  const { peoId } = useCurrentPeoStaff();
  const search = useEmployeeDataSearch({ scope: 'employerScope' });

  const inviteLinkArgs = {
    route: appRoutes.auth.signupEmployeePeo,
    paramKey: RouterEntitiesId.peo,
    entityId: peoId,
  };
  const copyInviteLink = useCopyEmployeeInviteLink(inviteLinkArgs);
  const resendInvite = useResendEmployeeInvite('peo');

  return (
    <PageSimpleLayout
      titleContent="Employees"
      topRightContent={
        hideInviteLink ? null : (
          <Button key="copyInviteLink" startIcon={<LinkIcon />} onClick={copyInviteLink}>
            Copy signup link
          </Button>
        )
      }
    >
      <EmployeeDataSearchScreen
        {...search}
        hideEmployerLiveIndicator
        allowEmailSearch
        inviteLinkArgs={inviteLinkArgs}
        inviteEmailOptionsScope="employerScope"
        sendInviteScope="peo"
        onResendInvite={resendInvite}
      />
    </PageSimpleLayout>
  );
};

const PeoPayrollProviderEmployeesPage: React.FC = () => {
  const { peoPayrollProviderId } = useCurrentPeoPayrollProviderStaff();
  const search = useEmployeeDataSearch({ scope: 'employerScope' });

  const inviteLinkArgs = {
    route: appRoutes.auth.signupEmployeePeoPayrollProvider,
    paramKey: RouterEntitiesId.peoPayrollProvider,
    entityId: peoPayrollProviderId,
  };
  const copyInviteLink = useCopyEmployeeInviteLink(inviteLinkArgs);

  return (
    <PageSimpleLayout
      titleContent="Employees"
      topRightContent={
        <Button key="copyInviteLink" startIcon={<LinkIcon />} onClick={copyInviteLink}>
          Copy signup link
        </Button>
      }
    >
      <EmployeeDataSearchScreen
        {...search}
        hideEmployerLiveIndicator
        allowEmailSearch
        inviteLinkArgs={inviteLinkArgs}
      />
    </PageSimpleLayout>
  );
};
